import React, {useMemo} from 'react';
import {QuestionAnswerEnum, QuestionReport} from '../../../../incase-domain/analytics';
import {IElementSequenceQuestion} from '../../../../incase-domain/elements/question/questionSequence';
import SequenceReport from '../../../../ui/pages/PresentationReportsPage/questions/SequenceReport';

interface DummySequenceQuestionReportProps {
    question: IElementSequenceQuestion;
}

const DUMMY_TOTAL_ANSWERS = 100;

const getOrdinalLabel = (position: number): string => {
    switch (position % 10) {
        case 1:
            return `${position}st`;
        case 2:
            return `${position}nd`;
        case 3:
            return `${position}rd`;
        default:
            return `${position}th`;
    }
};

const distributePercentages = (answerIndex: number, answerCount: number): Record<string, number> => {
    const weights = Array.from({length: answerCount}, (_, rankIndex) => answerCount - Math.abs(answerIndex - rankIndex));
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    let remainingPercentage = 100;

    return weights.reduce((percentages, weight, index) => {
        const rank = getOrdinalLabel(index + 1);
        const percentage = index === weights.length - 1 ? remainingPercentage : Math.round((weight * 100) / totalWeight);
        remainingPercentage -= percentage;

        return {
            ...percentages,
            [rank]: percentage,
        };
    }, {} as Record<string, number>);
};

function DummySequenceQuestionReport({question}: DummySequenceQuestionReportProps): JSX.Element | null {
    const dummyQuestionReport = useMemo<QuestionReport | null>(() => {
        if (!question.sequence.length) {
            return null;
        }

        const rankDistributionKeys = question.sequence.map((_, index) => getOrdinalLabel(index + 1));
        const rankDistributionData = question.sequence.map((answer, index) => {
            const rankingPercentages = distributePercentages(index, question.sequence.length);

            return {
                answerText: answer.text,
                rankingScore: (index + 1) * DUMMY_TOTAL_ANSWERS,
                averageRanking: index + 1,
                noOfAnswers: DUMMY_TOTAL_ANSWERS,
                ...rankingPercentages,
            };
        });

        return {
            elementId: question.id,
            question: question.question,
            answers: question.sequence.map((answer, index) => {
                const rankingPercentages = distributePercentages(index, question.sequence.length);

                return {
                    answerText: answer.text,
                    noOfAnswers: DUMMY_TOTAL_ANSWERS,
                    percentage: Math.round(DUMMY_TOTAL_ANSWERS / question.sequence.length),
                    type: QuestionAnswerEnum.DEBATABLE,
                    rankingScore: rankDistributionData[index].rankingScore,
                    averageRanking: rankDistributionData[index].averageRanking,
                    rankingPercentages,
                };
            }),
            slide: {id: '', name: ''},
            totalAnswers: DUMMY_TOTAL_ANSWERS,
            type: question.type,
            isDebatable: true,
            sequenceQuestionData: {
                rankDistributionData,
                rankDistributionKeys,
            },
        };
    }, [question]);

    if (!dummyQuestionReport) {
        return null;
    }

    return <SequenceReport questionReport={dummyQuestionReport} />;
}

export default DummySequenceQuestionReport;
